import { useCallback } from 'react';
import { join, filter } from 'ramda';
import useQueryParameters from 'hooks/useQueryParameters';
import useGeocoder, { GeocoderError } from 'hooks/requests/geocode';
import { NonClientError } from 'hooks/requests/fetchUtils';
import Coords from 'state/Coords';

export interface AddressObject {
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

const toAddressString = ({
  address1,
  address2,
  city,
  state,
  zip,
}: Partial<AddressObject>): string => {
  const fullAddress = join(
    ' ',
    filter(Boolean, [address1, address2, city, state, zip]),
  );
  const result = encodeURIComponent(fullAddress);
  return result;
};

type GeocodingHook = () => [
  boolean,
  (address: Partial<AddressObject>) => Promise<Coords>,
];

export class GeocodingError extends Error {}

const useGeocode: GeocodingHook = () => {
  const [geocoder] = useGeocoder();

  const { token } = useQueryParameters();
  const shouldGeocode = Boolean(token);

  const geocode = useCallback(
    async (address: Partial<AddressObject>) => {
      try {
        const addressString = toAddressString(address);
        const position = await geocoder(addressString);

        if (!position) {
          throw new GeocodingError(`Failed to geocode ${addressString}`);
        }

        return position;
      } catch (e) {
        if (e instanceof NonClientError || e instanceof GeocoderError) {
          throw new GeocodingError(e.message);
        }
        throw e;
      }
    },
    [geocoder],
  );
  return [shouldGeocode, geocode];
};

export default useGeocode;
